import React, { Fragment, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Table, Badge } from "reactstrap";
import { useDispatch } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../layout/breadcrumb";
import PreLoader from "../../../share-components/ui/preLoader";
import withReducer from "../../../store/withReducer";
import orderReducer from "../store";
import { getGojungoOrder } from "../store/orderSlice";

const dataMenu = [
  { type: 1, text: "Gojungo Orders", link: `${process.env.PUBLIC_URL}/order/gojungo-orders` },
  { type: 0, text: "Order View" },
];

const DISPLAY_CURRENCIES = ["ordered", "eur", "gbp"];

function GojungoOrderView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // The URL is the single source of truth for which currency is displayed — defaults to EUR
  // when the param is absent or not one of the recognised values.
  const requestedCurrency = (new URLSearchParams(location.search).get("display_currency") || "").toLowerCase();
  const displayCurrency = DISPLAY_CURRENCIES.includes(requestedCurrency) ? requestedCurrency : "eur";

  useEffect(() => {
    setLoading(true);
    dispatch(getGojungoOrder({ id, displayCurrency })).then((res) => {
      if (res.payload) setOrder(res.payload);
      setLoading(false);
    });
  }, [id, displayCurrency]);

  const setDisplayCurrency = (value) => {
    const params = new URLSearchParams(location.search);
    params.set("display_currency", value);
    history.push({ search: params.toString() });
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs title="Order View" data={dataMenu} />
        <Container fluid>
          <div style={{ minHeight: 400 }}><PreLoader /></div>
        </Container>
      </Fragment>
    );
  }

  if (!order) {
    return (
      <Fragment>
        <Breadcrumbs title="Order View" data={dataMenu} />
        <Container fluid>
          <p className="text-danger mt-4">Order not found.</p>
        </Container>
      </Fragment>
    );
  }

  const shippingAddr = order.shipping_address || {};
  const billingAddr  = order.billing_address  || {};
  const orderedCurrency = order.currency || "Ordered";
  const currencyLabel = displayCurrency === "gbp" ? "GBP" : displayCurrency === "eur" ? "EUR" : orderedCurrency;

  // Every amount is now pre-computed by the backend (ordered/EUR/GBP) — this just picks the
  // right sibling field for the selected currency, no client-side rate math anymore.
  const pick = (orderedVal, eurVal, gbpVal) => {
    if (displayCurrency === "eur") return parseFloat(eurVal ?? 0);
    if (displayCurrency === "gbp") return parseFloat(gbpVal ?? 0);
    return parseFloat(orderedVal ?? 0);
  };

  // Refunds have no ordered/native-currency leg at all — they're created exclusively from
  // Gojungo's EUR settlement data, converted straight to GBP, never in the order's own currency.
  // "Ordered" falls back to EUR here, the closest real figure to what currency this happened in.
  const pickRefund = (eurVal, gbpVal) =>
    displayCurrency === "gbp" ? parseFloat(gbpVal ?? 0) : parseFloat(eurVal ?? 0);

  const fmt = (n) => n.toFixed(2);
  const refundCurrencyLabel = displayCurrency === "gbp" ? "GBP" : "EUR";

  return (
    <Fragment>
      <Breadcrumbs title={`Order: ${order.gojungo_id}`} data={dataMenu} />

      <Container fluid className="product-wrapper">

        {/* Order Summary */}
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Order Details</h5>
              <div className="d-flex align-items-center">
                <div className="btn-group me-2" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${displayCurrency === "ordered" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setDisplayCurrency("ordered")}
                  >
                    {orderedCurrency}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${displayCurrency === "eur" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setDisplayCurrency("eur")}
                  >
                    EUR
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${displayCurrency === "gbp" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setDisplayCurrency("gbp")}
                  >
                    GBP
                  </button>
                </div>
                <button className="btn btn-sm btn-light" onClick={() => history.goBack()}>← Back</button>
              </div>
            </div>
            <Row>
              <Col md={3}><small className="text-muted">Order ID</small><p className="fw-bold mb-1">{order.gojungo_id}</p></Col>
              <Col md={3}><small className="text-muted">Channel No</small><p className="mb-1">{order.order_channel_no || '—'}</p></Col>
              <Col md={3}><small className="text-muted">Order Date</small><p className="mb-1">{order.order_date}</p></Col>
              <Col md={3}><small className="text-muted">Channel / Tenant</small><p className="mb-1">{order.channel?.toUpperCase()} / {order.tenant?.toUpperCase()}</p></Col>
              <Col md={3}><small className="text-muted">Currency</small><p className="mb-1">{order.currency}</p></Col>
              <Col md={3}><small className="text-muted">Currency Rate</small><p className="mb-1">{order.currency_rate}</p></Col>
              <Col md={3}><small className="text-muted">Total Price</small><p className="mb-1">{currencyLabel} {fmt(pick(order.total_price, order.total_price_eur, order.total_price_gbp))}</p></Col>
              <Col md={3}><small className="text-muted">Ship Country</small><p className="mb-1">{order.ship_country_code}</p></Col>
              <Col md={3}><small className="text-muted">Shipping Charges</small><p className="mb-1">{currencyLabel} {fmt(pick(order.shipping_charges, order.shipping_charges_eur, order.shipping_charges_gbp))}</p></Col>
              <Col md={3}><small className="text-muted">Shipping Discount</small><p className="mb-1">{currencyLabel} {fmt(pick(order.shipping_discount, order.shipping_discount_eur, order.shipping_discount_gbp))}</p></Col>
              <Col md={3}><small className="text-muted">Fulfillment</small><p className="mb-1">{order.fulfillmentType}</p></Col>
              <Col md={3}><small className="text-muted">Status</small><p className="mb-1">
                <Badge color={order.is_confirmed ? 'success' : 'warning'}>{order.is_confirmed ? 'Confirmed' : 'Pending'}</Badge>
                {order.is_test_order && <Badge color="secondary" className="ms-1">Test</Badge>}
              </p></Col>
            </Row>
          </CardBody>
        </Card>

        {/* Customer / Addresses */}
        <Row className="mb-3">
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Customer</h6>
                {order.customer ? (
                  <>
                    <p className="mb-1">{order.customer.firstName} {order.customer.lastName}</p>
                    <p className="mb-1 text-muted">{order.customer.email}</p>
                    <p className="mb-0 text-muted">{order.customer.phoneMobile}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Shipping Address</h6>
                {shippingAddr.firstName ? (
                  <>
                    <p className="mb-1">{shippingAddr.firstName} {shippingAddr.lastName}</p>
                    <p className="mb-1">{shippingAddr.streetNo}{shippingAddr.streetExtension ? ` ${shippingAddr.streetExtension}` : ''}</p>
                    <p className="mb-1">{shippingAddr.zip} {shippingAddr.city}</p>
                    <p className="mb-0">{shippingAddr.country}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Billing Address</h6>
                {billingAddr.firstName ? (
                  <>
                    <p className="mb-1">{billingAddr.firstName} {billingAddr.lastName}</p>
                    <p className="mb-1">{billingAddr.streetNo}{billingAddr.streetExtension ? ` ${billingAddr.streetExtension}` : ''}</p>
                    <p className="mb-1">{billingAddr.zip} {billingAddr.city}</p>
                    <p className="mb-0">{billingAddr.country}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Line Items */}
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">Line Items ({order.line_items?.length || 0})</h6>
            <div className="table-responsive">
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Item ID</th>
                    <th>SKU</th>
                    <th>Gojungo SKU</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Price ({currencyLabel})</th>
                    <th>Discount ({currencyLabel})</th>
                    <th>Total ({currencyLabel})</th>
                  </tr>
                </thead>
                <tbody>
                  {order.line_items?.length ? order.line_items.map((item) => {
                    const price = pick(item.price, item.price_eur, item.price_gbp);
                    const discount = pick(item.discountedPrice, item.discount_eur, item.discount_gbp);
                    return (
                      <tr key={item.id}>
                        <td>{item.item_id}</td>
                        <td>{item.sku || <span className="text-danger fw-bold">Not Found</span>}</td>
                        <td>{item.gojungo_sku}</td>
                        <td>{item.billing_text}</td>
                        <td>{item.quantity}</td>
                        <td>{fmt(price)}</td>
                        <td>{fmt(discount)}</td>
                        <td>{fmt(price * parseInt(item.quantity || 0))}</td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={8} className="text-center text-muted">No line items</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Totals summary */}
            {(() => {
              const items = order.line_items || [];
              const total = items.reduce((sum, i) => sum + pick(i.price, i.price_eur, i.price_gbp) * parseInt(i.quantity || 0), 0);
              const totalDiscount = items.reduce((sum, i) => sum + pick(i.discountedPrice, i.discount_eur, i.discount_gbp), 0);
              const shipping = pick(order.shipping_charges, order.shipping_charges_eur, order.shipping_charges_gbp);
              const grandTotal = total - totalDiscount + shipping;
              return (
                <div className="d-flex justify-content-end mt-3">
                  <table style={{ minWidth: 280 }}>
                    <tbody>
                      <tr>
                        <td className="text-muted pe-4">Total</td>
                        <td className="text-end fw-bold">{currencyLabel} {fmt(total)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-4">Total Discount</td>
                        <td className="text-end fw-bold">- {currencyLabel} {fmt(totalDiscount)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-4">Shipping Charges</td>
                        <td className="text-end fw-bold">{currencyLabel} {fmt(shipping)}</td>
                      </tr>
                      <tr className="border-top">
                        <td className="text-muted pe-4 pt-2"><strong>Grand Total</strong></td>
                        <td className="text-end fw-bold pt-2">{currencyLabel} {fmt(grandTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardBody>
        </Card>

        {/* Refunds */}
        {order.refunds?.length > 0 && (
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">Refunds ({order.refunds.length})</h6>
                {displayCurrency === "ordered" && (
                  <small className="text-muted">Refunds settle in EUR — showing EUR figures</small>
                )}
              </div>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>SKU</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Return Total ({refundCurrencyLabel})</th>
                      <th>Discount ({refundCurrencyLabel})</th>
                      <th>Net Amount ({refundCurrencyLabel})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.refunds.map((r) => {
                      const returnTotal = pickRefund(r.return_total_eur, r.return_total);
                      const discount = pickRefund(r.discountedPrice_eur, r.discount_gbp);
                      return (
                        <tr key={r.id}>
                          <td>{r.sku}</td>
                          <td>{r.billing_text}</td>
                          <td>{r.quantity}</td>
                          <td>{fmt(returnTotal)}</td>
                          <td>{fmt(discount)}</td>
                          <td>{fmt(returnTotal - discount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Cancelled Items */}
        {order.line_items?.some((i) => i.is_cancelled) && (
          <Card>
            <CardBody>
              <h6 className="mb-3">
                Cancelled Items ({order.line_items.filter((i) => i.is_cancelled).length})
              </h6>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Item ID</th>
                      <th>SKU</th>
                      <th>Gojungo SKU</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price ({currencyLabel})</th>
                      <th>Discount ({currencyLabel})</th>
                      <th>Total ({currencyLabel})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.line_items.filter((i) => i.is_cancelled).map((item) => {
                      const price = pick(item.price, item.price_eur, item.price_gbp);
                      const discount = pick(item.discountedPrice, item.discount_eur, item.discount_gbp);
                      return (
                        <tr key={item.id}>
                          <td>{item.item_id}</td>
                          <td>{item.sku || <span className="text-danger fw-bold">Not Found</span>}</td>
                          <td>{item.gojungo_sku}</td>
                          <td>{item.billing_text}</td>
                          <td>{item.quantity}</td>
                          <td>{fmt(price)}</td>
                          <td>{fmt(discount)}</td>
                          <td>{fmt(price * parseInt(item.quantity || 0))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

      </Container>
    </Fragment>
  );
}

export default withReducer([{ order: orderReducer }])(GojungoOrderView);
